import style from "@/styles/pages/Dashboard/Dashboard.module.css";
import { useDispatch, useSelector } from "react-redux";
import { replace, useNavigate } from "react-router-dom";
import { flushSync } from 'react-dom';
import { createApiClient } from "@/lib/Requests";
import { useEffect, useRef, useState } from "react";
import {
    clearApplicationState,
    setAmortizationLength,
    setApplicationCategory,
    setApplicationEndpointReached,
    setApplicationId,
    setApplicationProgress,
    setApplicationStatus,
    setDownPayment,
    setIdVerificationSubmitted,
    setIsCoApplicant,
    setJointAccepted,
    setJointCode,
    setJointPrimaryApplicant,
    setJointRelationship,
    setTermLength,
    setTotalLoan,
    setUnderStaffReview,
} from "@/slices/applicationSlice";
import { RootState } from "@/store/dtm";
import FadeTransition from "@/styles/animation/FadeTransition";
import clsx from "clsx";
import { americanStatesAbbreviationList, generateSessionId, isMobileDevice, setClickInsightEvents, setSingleClickInsightEvent } from "@/lib/util";
import { setAlloySessionId, setConsentCompleted, setIdVerificationStatus, setResidenceMonths, setResidenceYears, setResidentStatus } from "@/slices/applicantSlice";
import { applicationCategory, verificationCategory } from "@/types/applicationType";
import { setCoapplicantComplete, setCredentialsComplete, setFinalizeAccoutComplete, setHomeDetailsComplete, setMortgageComplete, setProfileComplete } from "@/slices/progressSlice";
import { setStreetNumber, setStreetName, setStreetType, setStreetDirection, setUnitType, setUnitNumber, setCity, setProvince, setPostalCode, setCountry, setAddressId, selectHomeAddress, homeAddressState } from "@/slices/homeAddressSlice";
import { addressInfo, addPreviousAddress, clearPreviousAddressState } from "@/slices/PreviousAddressSlice";
import { clearPropertyInfoState, setClosingDate, setFinancingConditionDate, setFinancingConditionExists, setFundingDate, setHeatingCost, setHeatingType, setLivableArea, setLivableAreaUnit, setMaintenanceFee, setMonthlyRent, setMortgageBalance, setMortgageHolder, setNumOfRentalProperties, setNumOfRentalUnits, setOriginalPurchasePrice, setOwnershipPercentage, setOwnOtherRentalProperties, setPropertyTax, setPropertyValue, setPurchasePrice, setPurposeOfProperty, setRentOut, setSeparateUnit, setSubjectPropertyRentalUnits } from "@/slices/propertyInfoSlice";
import { setPAStreetNumber, setPAStreetName, setPAUnitNumber, setPACity, setPAProvince, setPAPostalCode, setPACountry, setPAStreetDirection, setPAStreetType, setPAUnitType } from "@/slices/propertyAddressSlice";
import { setEmploymentId, setEmploymentIncome, setEmploymentStatus, setEmploymentType, setPayrollFrequency, setPayrollType } from "@/slices/employmentSlice";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setCachedAssets, setCachedCurrentEmployment, setFinancialInstitutions, setOccupations, setCachedExpenses } from "@/slices/sessionDataSlice";
import { coapplicant, addCoApplicant, clearCoApplicantState, selectCoApplicant } from "@/slices/coApplicantSlice";
import { motion } from "framer-motion";
import { Dialog } from "primereact/dialog";
import { ApplicationDto, GetDealById, InitIdVerificationDto } from "@/lib/DTMWebAPIClient.generated";
import { streetTypes } from "@/ts/streetTypes";
import { addCurrentEmployment, clearCurrentEmploymentState, currentEmploymentInfo, IncomeInfo, selectCurrentEmployment } from "@/slices/currentEmploymentSlice";
import { addPreviousEmployment, clearPreviousEmploymentState, toggleNoPreviousEmployment } from "@/slices/previousEmploymentSlice";
import { addAsset, assetInfo, clearAssetState, setAssetInfo } from "@/slices/assetSlice";
import dayjs from "dayjs";
import { addExpense, clearExpenseState, expenseInfo } from "@/slices/expenseSlice";
import { set } from "react-hook-form";
import { clearLegalTermsState } from "@/slices/legalTermsSlice";

export default function Dashboard() {
    const credential = useSelector(
        (state: RootState) => state.credential.credential
    );
    const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
    const client = createApiClient({ timeout: 60000, token: credential.accessToken });
    const [fetchingApplicantStatus, setFetchingApplicantStatus] = useState(true);
    const [fetchingApplicationStatus, setFetchingApplicationStatus] = useState(true);
    const [initiatingNewApplication, setInitiatingNewApplication] = useState(false);
    const [assetsFetched, setAssetsFetched] = useState(false);
    const dealDetached = useRef(false);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const assets = useSelector((state: RootState) => state.assets.assets);
    const expenses = useSelector((state: RootState) => state.expenses.expenses);
    const homeAddressRef = useRef<homeAddressState>({
        address: {
            id: 0,
            streetNumber: "",
            streetName: "",
            streetType: "",
            streetDirection: "",
            unitType: "",
            unitNumber: "",
            city: "",
            province: "",
            postalCode: "",
            country: "",
        },
        mailingAddressSame: true
    });
    const coapplicants = useSelector(selectCoApplicant).coapplicants;
    const legalTerms = useSelector(
        (state: RootState) => state.legalTerms
    );
    const currentEmployment = useSelector(selectCurrentEmployment).employments;
    const [applicationDialogVisible, setApplicationDialogVisible] = useState(false);
    const getAmericanStates = americanStatesAbbreviationList();
    const footerContent = (
        <div className="justify-center grid">
            <button
                type="submit"
                className="primary mt-5 w-169 sm:w-28 h-11 px-6 mx-5"
                style={{ minWidth: 170 }}
                onClick={() => {
                    dispatch(setUnderStaffReview(true));
                    setApplicationDialogVisible(false)
                }}
            >
                Go to Dashboard
            </button>
        </div>
    );

    const completeRequiredItems = () => {
        if (application.applicationEndpointReached) {
            applicant.idVerificationStatus == verificationCategory.Softfail ?
                navigate("/verify-id/verify-identification") :
                applicant.idVerificationStatus == verificationCategory.Hardfail ?
                    navigate("/single/application-error") : navigate("/verify-id/accept-terms");
        } else {
            resumeApplication()
        }
    }

    const startNewApplication = () => {
        let newAlloySessionId = generateSessionId(application.isCoApplicant);
        setInitiatingNewApplication(true);
        setFetchingApplicantStatus(true);

        flushSync(() => {
            dispatch(clearApplicationState());
            dispatch(clearPropertyInfoState())
            dispatch(clearCoApplicantState());
            dispatch(clearAssetState());
            dispatch(clearExpenseState());
            dispatch(clearPreviousEmploymentState());
            dispatch(clearLegalTermsState());
            dispatch(setApplicationStatus(""));
        });

        dispatch(setAlloySessionId(newAlloySessionId));

        client.apiDealsInitiate().then(result => {
            //Reset application progress and other info
            let localInfo: any = {}
            let updatedApplication = {
                ...application,
                applicationCategory: 0,
                purchaseTypeSelected: false,
                status: "",
                alloyApplicationToken: "",
                applicationEndpointReached: false,
                idVerificationSubmitted: false,
                underStaffReview: false
            };
            let updatedApplicant = {
                ...applicant,
                alloySessionId: newAlloySessionId,
                residentStatus: "",
                residenceYears: 0,
                residenceMonths: 0
            };
            localInfo.applicant = updatedApplicant;
            localInfo.application = updatedApplication;
            localInfo.currentEmployment = currentEmployment;
            localInfo.expenses = expenses;
            localInfo.coapplicants = [];

            if (!applicant.isProspect) {
                let updatedLegalTerms = {
                    ...legalTerms,
                    fieldsCompleted: true
                }
                localInfo.legalTerms = updatedLegalTerms;
            }

            client.apiApplicationsSave(new ApplicationDto({
                applicationData: JSON.stringify(localInfo)
            })).catch((error) => {
                console.log("Error saving application info");
            });

            client.apiIdverificationInit(new InitIdVerificationDto({
                alloySessionId: newAlloySessionId,
                applicationId: 0, //This will be updated once the deal is created on Trellis
                applicantId: applicant.id,
                status: "PENDING",
                phoneVerified: applicant.twilioResult.codeVerified
            })).then(function (data) {

                //Take user to public website to retrieve product ID
                window.location.replace('https://www.meridiancu.ca/personal/mortgages');
            }).catch((error) => {
                console.log(error);

                //Take user to public website to retrieve product ID
                window.location.replace('https://www.meridiancu.ca/personal/mortgages');
            });

        }).catch((error) => {
            console.log("Error initiating new application", error);
            setInitiatingNewApplication(false);
            setFetchingApplicantStatus(false);
        });
    }

    const detachFromDeal = () => {
        let newAlloySessionId = generateSessionId(false);
        dispatch(setApplicationId(0));
        dispatch(setApplicationEndpointReached(false));
        dispatch(setJointCode(""));
        dispatch(setJointRelationship(""));
        dispatch(setJointPrimaryApplicant(""));
        dispatch(clearPropertyInfoState());
        dispatch(clearLegalTermsState());

        dispatch(setAlloySessionId(newAlloySessionId));

        client.apiDealsDetach().then(result => {
            //Reset application progress and other info
            let localInfo: any = {}
            let updatedApplication = {
                ...application,
                isCoApplicant: false,
                applicationCategory: 0,
                purchaseTypeSelected: false,
                alloyApplicationToken: "",
                status: "",
                applicationEndpointReached: false,
                idVerificationSubmitted: false,
                underStaffReview: false
            };
            let updatedApplicant = {
                ...applicant,
                alloySessionId: newAlloySessionId,
                residentStatus: "",
                residenceYears: 0,
                residenceMonths: 0
            };
            localInfo.applicant = updatedApplicant;
            localInfo.application = updatedApplication;
            localInfo.currentEmployment = currentEmployment;
            localInfo.expenses = expenses;
            localInfo.coapplicants = [];

            if (!applicant.isProspect) {
                let updatedLegalTerms = {
                    ...legalTerms,
                    fieldsCompleted: true
                }
                localInfo.legalTerms = updatedLegalTerms;
            }

            client.apiIdverificationInit(new InitIdVerificationDto({
                alloySessionId: newAlloySessionId,
                applicationId: 0, //This will be updated once the deal is created on Trellis
                applicantId: applicant.id,
                status: "PENDING",
                phoneVerified: applicant.twilioResult.codeVerified
            })).then(function (data) {
                client.apiApplicationsSave(new ApplicationDto({
                    applicationData: JSON.stringify(localInfo)
                })).catch((error) => {
                    console.log("Error saving application info");
                });

            }).catch((error) => {
                console.log(error);

            });

        }).catch((error) => {
            console.log("Error detaching from application", error);
            setInitiatingNewApplication(false);
            setFetchingApplicantStatus(false);
        });
    }

    useEffect(() => {
        document.title = "Meridian Lending - Home";
        client
            .apiDataOccupations()
            .then(function (data) {
                dispatch(setOccupations(data != null && data != undefined ? data : []));
            })
            .catch((error) => {
                navigate("/error/3");
            });
        client.apiDataFinancialinstitutions()
            .then(function (data) {
                dispatch(setFinancialInstitutions(data != null && data != undefined ? data : []));
            })
            .catch((error) => {
                navigate("/error/3");
            });

        const fetchApplicantData = async () => {
            //Fetch Saved Addresses
            if (applicant && applicant.id != 0) {
                await client.apiApplicantsAddressesGet().then(a => {
                    dispatch(clearPreviousAddressState());
                    let prevAddressIndex = 0;
                    a.forEach(address => {
                        if (address.residenceStatusType_Name != undefined) {
                            //Home Address
                            dispatch(setAddressId(address.id ? address.id : 0));
                            dispatch(setStreetNumber(address.streetNumber ? address.streetNumber : ""));
                            dispatch(setStreetName(address.streetName ? address.streetName : ""));
                            dispatch(setStreetType(address.streetType ? address.streetType : ""));
                            dispatch(setStreetDirection(address.streetDirection ? address.streetDirection : ""));
                            dispatch(setUnitType(address.unitType ? address.unitType : ""));
                            dispatch(setUnitNumber(address.unitNumber ? address.unitNumber : ""));
                            dispatch(setCity(address.city));

                            //If the country is set to United States but the province/state does not match a US state, do not set the province value as it is likely a Canadian province that was not properly cleared
                            if (address.country == "United States" && !getAmericanStates.includes(address.region ? address.region : "")) {
                                dispatch(setProvince(""));
                            } else {
                                dispatch(setProvince(address.region ? address.region : ""));
                            }
                            dispatch(setPostalCode(address.postalCode ? address.postalCode : ""));
                            dispatch(setCountry(address.country));

                            dispatch(setResidentStatus(address.residenceStatusType_Name));
                            if (address.timeAtResidenceInMonths != 0) {
                                let yearCount = address.timeAtResidenceInMonths ? Math.floor(address.timeAtResidenceInMonths / 12) : 0;
                                let monthCount = address.timeAtResidenceInMonths ? address.timeAtResidenceInMonths % 12 : 0;
                                dispatch(setResidenceYears(yearCount));
                                dispatch(setResidenceMonths(monthCount));
                            }

                            //Attempt to parse the street type based on whether it contains multiple words
                            if ((address.streetType == undefined || address.streetType == "") && address.streetName && address.streetName.split(" ").length > 1) {
                                let streetNameComponents = address.streetName.split(" ");
                                let newStreetName = "";
                                for (let i = 0; i < streetNameComponents.length - 1; i++) {
                                    newStreetName += streetNameComponents[i] + " ";
                                }
                                let streetType = streetNameComponents[streetNameComponents.length - 1].toUpperCase();
                                dispatch(setStreetType(streetTypes.includes(streetType) ? streetType : ""));
                                dispatch(setStreetName(newStreetName.trim()));
                            } else if (address.streetType != undefined && address.streetType != "") {
                                dispatch(setStreetType(address.streetType));
                            }
                            homeAddressRef.current = {
                                address: {
                                    id: address.id ? address.id : 0,
                                    streetNumber: address.streetNumber ? address.streetNumber : "",
                                    streetName: address.streetName ? address.streetName : "",
                                    streetType: address.streetType ? address.streetType : "",
                                    streetDirection: address.streetDirection ? address.streetDirection : "",
                                    unitType: address.unitType ? address.unitType : "",
                                    unitNumber: address.unitNumber ? address.unitNumber : "",
                                    city: address.city,
                                    province: address.region ? address.region : "",
                                    postalCode: address.postalCode ? address.postalCode : "",
                                    country: address.country
                                },
                                mailingAddressSame: true
                            }

                        } else {
                            //Previous Addresses will not have a residence status
                            prevAddressIndex++;
                            let newPrevAddress: addressInfo = {
                                id: address.id ? address.id : 0,
                                displayId: prevAddressIndex.toString(),
                                streetNumber: address.streetNumber ? address.streetNumber : "",
                                streetName: address.streetName ? address.streetName : "",
                                streetType: address.streetType ? address.streetType : "",
                                streetDirection: address.streetDirection ? address.streetDirection : "",
                                unitType: address.unitType ? address.unitType : "",
                                unitNumber: address.unitNumber ? address.unitNumber : "",
                                city: address.city,
                                province: address.region ? address.region : "",
                                postalCode: address.postalCode ? address.postalCode : "",
                                country: address.country,
                                manualAddressEntered: false,
                                yearsLivedOn: address.timeAtResidenceInMonths ? Math.floor(address.timeAtResidenceInMonths / 12) : 0,
                                monthsLivedOn: address.timeAtResidenceInMonths ? address.timeAtResidenceInMonths % 12 : 0
                            }
                            dispatch(addPreviousAddress(newPrevAddress));
                        }
                    })
                }).catch((error) => {
                    navigate("/error/3");
                });

                await client.apiIdverificationIdverificationapplicant().then(idVerification => {
                    if (idVerification) {
                        switch (idVerification.status) {
                            case "Processing":
                                dispatch(setIdVerificationStatus(verificationCategory.Processing));
                                dispatch(setIdVerificationSubmitted(true));
                                break;
                            case "Pass":
                                dispatch(setIdVerificationStatus(verificationCategory.Pass));
                                dispatch(setIdVerificationSubmitted(true));
                                break;
                            case "Softfail":
                                dispatch(setIdVerificationStatus(verificationCategory.Softfail));
                                dispatch(setIdVerificationSubmitted(true));
                                break;
                            case "Hardfail":
                                dispatch(setIdVerificationStatus(verificationCategory.Hardfail));
                                dispatch(setIdVerificationSubmitted(true));
                                break;
                            default:
                                dispatch(setIdVerificationStatus(verificationCategory.Pending));
                                break;
                        }
                    }
                    setFetchingApplicantStatus(false);
                }).catch((error) => {
                    setFetchingApplicantStatus(false);
                    //navigate("/error/3");
                });
            } else {
                setFetchingApplicantStatus(false);
            }
        }

        fetchApplicantData();
        fetchEmploymentData();
        fetchDealData();
    }, []);

    const fetchEmploymentData = () => {
        //Fetch Employment Info
        client.apiEmploymentsGet().then(employment => {
            dispatch(clearCurrentEmploymentState());
            dispatch(clearPreviousEmploymentState());

            client.apiApplicationsFetch().then((result) => {
                if (result.previousEmployment != undefined) {
                    dispatch(toggleNoPreviousEmployment(result.previousEmployment.noPreviousEmployment));
                }
            }).catch((error) => {
                console.log("Error fetching employment info");
            });

            if (employment && employment.length > 0) {
                let employmentInfo = employment[0];
                dispatch(setEmploymentId(employmentInfo.id ? employmentInfo.id : 0));
                dispatch(setEmploymentStatus(employmentInfo.employmentType_Name ? employmentInfo.employmentType_Name : ""));
                dispatch(setEmploymentType(employmentInfo.employmentPositionType_Name ? employmentInfo.employmentPositionType_Name : ""));
                if (employmentInfo.employmentIncomes != undefined && employmentInfo.employmentIncomes.length > 0) {
                    let income = employmentInfo.employmentIncomes[0];
                    dispatch(setPayrollType(income.incomeType_Name ? income.incomeType_Name : ""));
                    dispatch(setEmploymentIncome(income.amountPerFrequency ? income.amountPerFrequency : 0));
                    dispatch(setPayrollFrequency(income.frequencyType_Name ? income.frequencyType_Name : ""));
                }
                //dispatch(setOccupation(employmentInfo.employer_Occupation_Name ? employmentInfo.employer_Occupation_Name : ""));
            }

            if (employment != undefined && employment.length > 0) {
                let currentEmpIndex = 0;
                let prevEmpIndex = 0;
                let cachedCurrentEmployment: currentEmploymentInfo[] = [];
                employment.forEach(emp => {
                    //Extract incomes from the employment info
                    let regIncome = {
                        id: 0,
                        amount: 0,
                        frequency: ""
                    }
                    let overtimeIncome = {
                        id: 0,
                        amount: 0,
                        frequency: ""
                    }
                    let bonusIncome = {
                        id: 0,
                        amount: 0,
                        frequency: ""
                    }
                    let commissionIncome = {
                        id: 0,
                        amount: 0,
                        frequency: ""
                    }
                    let tipsIncome = {
                        id: 0,
                        amount: 0,
                        frequency: ""
                    }
                    let payrollType = "";
                    if (emp.employmentIncomes != undefined && emp.employmentIncomes.length > 0) {
                        payrollType = emp.employmentIncomes[0].incomeType_Name ? emp.employmentIncomes[0].incomeType_Name : "";
                    }

                    let otherIncomes: IncomeInfo[] = [];
                    if (emp.employmentType_Name === "retired") {
                        emp.employmentIncomes?.forEach((income, index) => {
                            otherIncomes.push({
                                id: income.id ? income.id : 0,
                                displayId: (index + 1).toString(),
                                type: income.incomeType_Name ? income.incomeType_Name : "",
                                amount: income.amountPerFrequency ? income.amountPerFrequency : 0,
                                amountText: income.amountPerFrequency ? income.amountPerFrequency.toString() : "",
                                frequency: income.frequencyType_Name ? income.frequencyType_Name : ""
                            });
                        });
                    } else {
                        emp.employmentIncomes?.forEach(income => {
                            if (income.primaryIncomeSource) {
                                regIncome = {
                                    id: income.id ? income.id : 0,
                                    amount: income.amountPerFrequency ? income.amountPerFrequency : 0,
                                    frequency: income.frequencyType_Name ? income.frequencyType_Name : ""
                                }
                            } else {
                                switch (income.incomeType_Name) {
                                    case "salary":
                                        regIncome = {
                                            id: income.id ? income.id : 0,
                                            amount: income.amountPerFrequency ? income.amountPerFrequency : 0,
                                            frequency: income.frequencyType_Name ? income.frequencyType_Name : ""
                                        }
                                        break;
                                    case "overtime":
                                        overtimeIncome = {
                                            id: income.id ? income.id : 0,
                                            amount: income.amountPerFrequency ? income.amountPerFrequency : 0,
                                            frequency: income.frequencyType_Name ? income.frequencyType_Name : ""
                                        }
                                        break;
                                    case "bonus":
                                        bonusIncome = {
                                            id: income.id ? income.id : 0,
                                            amount: income.amountPerFrequency ? income.amountPerFrequency : 0,
                                            frequency: income.frequencyType_Name ? income.frequencyType_Name : ""
                                        }
                                        break;
                                    case "commission":
                                        commissionIncome = {
                                            id: income.id ? income.id : 0,
                                            amount: income.amountPerFrequency ? income.amountPerFrequency : 0,
                                            frequency: income.frequencyType_Name ? income.frequencyType_Name : ""
                                        }
                                        break;
                                    case "declaredtips":
                                        tipsIncome = {
                                            id: income.id ? income.id : 0,
                                            amount: income.amountPerFrequency ? income.amountPerFrequency : 0,
                                            frequency: income.frequencyType_Name ? income.frequencyType_Name : ""
                                        }
                                        break;
                                    default:
                                        regIncome = {
                                            id: income.id ? income.id : 0,
                                            amount: income.amountPerFrequency ? income.amountPerFrequency : 0,
                                            frequency: income.frequencyType_Name ? income.frequencyType_Name : ""
                                        }
                                        break;
                                }
                            }
                        });
                    }

                    let newEmploymentInfo: currentEmploymentInfo = {
                        id: emp.id ? emp.id : 0,
                        displayId: "",
                        visible: false,
                        status: emp.employmentType_Name ? emp.employmentType_Name : "",
                        type: emp.employmentPositionType_Name ? emp.employmentPositionType_Name : "",
                        occupation: emp.employer_Occupation_Name ? emp.employer_Occupation_Name : "",
                        occupationIndustry: emp.employer_Occupation_Industry ? emp.employer_Occupation_Industry : "",
                        jobTitle: emp.jobTitle ? emp.jobTitle : "",
                        ownershipType: emp.ownership_OwnershipType_Name ? emp.ownership_OwnershipType_Name : "",
                        businessType: emp.ownership_BusinessType ? emp.ownership_BusinessType : "",
                        hasBusinessLending: emp.ownership_HasLendingWithMeridian ? "yes" : "no",
                        industryYears: emp.monthsInIndustry ? Math.floor(emp.monthsInIndustry / 12) : 0,
                        industryMonths: emp.monthsInIndustry ? emp.monthsInIndustry % 12 : 0,
                        employmentYears: emp.employmentType_Name == "retired" || emp.employmentType_Name == "unemployed" ? 0 : emp.monthsAtEmployment ? Math.floor(emp.monthsAtEmployment / 12) : 0,
                        employmentMonths: emp.employmentType_Name == "retired" || emp.employmentType_Name == "unemployed" ? 0 : emp.monthsAtEmployment ? emp.monthsAtEmployment % 12 : 0,
                        notWorkingYears: emp.employmentType_Name == "retired" || emp.employmentType_Name == "unemployed" ? emp.monthsAtEmployment ? Math.floor(emp.monthsAtEmployment / 12) : 0 : 0,
                        notWorkingMonths: emp.employmentType_Name == "retired" || emp.employmentType_Name == "unemployed" ? emp.monthsAtEmployment ? emp.monthsAtEmployment % 12 : 0 : 0,
                        payrollType: payrollType,
                        income: regIncome.amount,
                        incomeId: regIncome.id,
                        incomeText: regIncome.amount != 0 ? regIncome.amount.toString() : "",
                        payrollFrequency: regIncome.frequency,
                        noRetirementIncome: emp.employmentType_Name == "retired" && (emp.employmentIncomes == undefined || emp.employmentIncomes.length == 0),
                        hasOvertimeIncome: overtimeIncome.id != 0,
                        overtimeIncomeId: overtimeIncome.id,
                        overtimeIncome: overtimeIncome.amount,
                        overtimeIncomeText: overtimeIncome.amount != 0 ? overtimeIncome.amount.toString() : "",
                        overtimeIncomeFrequency: overtimeIncome.frequency,
                        hasBonusIncome: bonusIncome.id != 0,
                        bonusIncomeId: bonusIncome.id,
                        bonusIncome: bonusIncome.amount,
                        bonusIncomeText: bonusIncome.amount != 0 ? bonusIncome.amount.toString() : "",
                        bonusIncomeFrequency: bonusIncome.frequency,
                        hasCommissionIncome: commissionIncome.id != 0,
                        commissionIncomeId: commissionIncome.id,
                        commissionIncome: commissionIncome.amount,
                        commissionIncomeText: commissionIncome.amount != 0 ? commissionIncome.amount.toString() : "",
                        commissionIncomeFrequency: commissionIncome.frequency,
                        hasTipsIncome: tipsIncome.id != 0,
                        tipsIncomeId: tipsIncome.id,
                        tipsIncome: tipsIncome.amount,
                        tipsIncomeText: tipsIncome.amount != 0 ? tipsIncome.amount.toString() : "",
                        tipsIncomeFrequency: tipsIncome.frequency,
                        otherIncome: otherIncomes,

                        employerName: emp.employer_Name ? emp.employer_Name : "",
                        employerPhone: emp.employer_Phone ? emp.employer_Phone : "",
                        employerAddress: {
                            displayId: "",
                            id: 0,
                            streetNumber: emp.employer_Address_StreetNumber ? emp.employer_Address_StreetNumber : "",
                            streetName: emp.employer_Address_StreetName ? emp.employer_Address_StreetName : "",
                            streetType: emp.employer_Address_StreetType ? emp.employer_Address_StreetType : "",
                            streetDirection: emp.employer_Address_StreetDirection ? emp.employer_Address_StreetDirection : "",
                            unitType: emp.employer_Address_UnitType ? emp.employer_Address_UnitType : "",
                            unitNumber: emp.employer_Address_UnitNumber ? emp.employer_Address_UnitNumber : "",
                            city: emp.employer_Address_City ? emp.employer_Address_City : "",
                            province: emp.employer_Address_Region ? emp.employer_Address_Region : "",
                            postalCode: emp.employer_Address_PostalCode ? emp.employer_Address_PostalCode : "",
                            country: emp.employer_Address_Country ? emp.employer_Address_Country : "",
                        },
                        manualAddressEntered: false,
                        isProbation: emp.onProbation ? "yes" : "no",
                        isFamilyOwned: emp.familyOwned ? "yes" : "no",
                        isOnLeave: emp.onParentalLeave || emp.onOtherLeave ? "yes" : "no",
                        leaveType: emp.onParentalLeave ? "parental" : emp.onOtherLeave ? "other" : "",
                        otherLeaveDescription: emp.leaveDescription ? emp.leaveDescription : "",
                        workReturnDate: emp.returnToWorkDate ? emp.returnToWorkDate.format("DD/MM/YYYY") : "",
                    }
                    if (emp.previousEmployment) {
                        prevEmpIndex++;
                        newEmploymentInfo.displayId = prevEmpIndex.toString();
                        newEmploymentInfo.employerAddress.displayId = prevEmpIndex.toString();
                        newEmploymentInfo.visible = prevEmpIndex == 1 ? true : false;
                        dispatch(addPreviousEmployment(newEmploymentInfo));
                    } else {
                        currentEmpIndex++;
                        newEmploymentInfo.displayId = currentEmpIndex.toString();
                        newEmploymentInfo.employerAddress.displayId = currentEmpIndex.toString();
                        newEmploymentInfo.visible = currentEmpIndex == 1 ? true : false;
                        dispatch(addCurrentEmployment(newEmploymentInfo));
                        cachedCurrentEmployment.push(newEmploymentInfo);
                    }
                });
                dispatch(setCachedCurrentEmployment(cachedCurrentEmployment));
            } else {
                dispatch(setCachedCurrentEmployment([]));
            }
        }).catch((error) => {
            //navigate("/error/3");
        });
    }

    const fetchDealData = () => {
        client.apiDealsGet().then(deal => {
            if (deal != undefined) {
                dispatch(setApplicationStatus(deal.status ? deal.status : ""));

                if (deal.status && deal.status == "Cancelled") {
                    dispatch(setApplicationId(0));
                    dispatch(setApplicationProgress(0));
                    dispatch(setJointCode(""));
                    dispatch(setJointRelationship(""));
                    dispatch(setJointPrimaryApplicant(""));
                    dispatch(setJointAccepted(false));
                    dispatch(setApplicationEndpointReached(false));
                    dispatch(setIdVerificationStatus(verificationCategory.Pending));
                    dispatch(setIdVerificationSubmitted(false));
                    setFetchingApplicationStatus(false);
                } else {
                    //Fetch Consultation and Property
                    client.apiDealsApplicationsMortgages(undefined, undefined).then(async application => {
                        if (application && application.dealId != undefined) {
                            dispatch(setApplicationId(application.dealId ? application.dealId : 0));
                            dispatch(setDownPayment(application.downPaymentAmount ? application.downPaymentAmount : 0));
                            if (application.products && application.products.length > 0) {
                                dispatch(setTermLength(application.products[0].termInMonths ? application.products[0].termInMonths / 12 : 0));
                                dispatch(setAmortizationLength(application.products[0].amortizationInMonths ? application.products[0].amortizationInMonths / 12 : 0));
                            }
                            switch (application.purposeType) {
                                case "Purchase":
                                    dispatch(setApplicationCategory(applicationCategory.Buy));
                                    break;
                                case "Refinance":
                                    dispatch(setApplicationCategory(applicationCategory.Refinance));
                                    break;
                                case "Transfer":
                                    dispatch(setApplicationCategory(applicationCategory.Transfer));
                                    break;
                            }
                            dispatch(setMortgageBalance(application.remainingBalanceAmount ? application.remainingBalanceAmount : 0));
                            dispatch(setTotalLoan(application.totalLoanAmount ? application.totalLoanAmount : 0));
                            if (application.subjectProperty) {
                                //Fetch other property details (i.e. heating cost, property tax, etc.)
                                dispatch(setPurposeOfProperty(application.subjectProperty.occupancyType ? application.subjectProperty.occupancyType : ""));
                                dispatch(setPurchasePrice(application.subjectProperty.purchasePrice ? application.subjectProperty.purchasePrice : 0));
                                dispatch(setOriginalPurchasePrice(application.subjectProperty.originalPurchasePrice ? application.subjectProperty.originalPurchasePrice : 0));
                                dispatch(setPropertyValue(application.subjectProperty.purchasePrice ? application.subjectProperty.purchasePrice : 0));
                                dispatch(setMortgageHolder(application.subjectProperty.mortgageHolder ? application.subjectProperty.mortgageHolder : ""));
                                dispatch(setPropertyTax(application.subjectProperty.propertyTax ? application.subjectProperty.propertyTax : 0));
                                dispatch(setLivableArea(application.subjectProperty.livableArea ? application.subjectProperty.livableArea : 0));
                                dispatch(setLivableAreaUnit(application.subjectProperty.livableAreaType ? application.subjectProperty.livableAreaType : ""));
                                dispatch(setHeatingCost(application.subjectProperty.heatingCost ? application.subjectProperty.heatingCost : 0));
                                dispatch(setHeatingType(application.subjectProperty.heatingType ? application.subjectProperty.heatingType : ""));
                                dispatch(setMaintenanceFee(application.subjectProperty.condoFees ? application.subjectProperty.condoFees : 0));
                                dispatch(setRentOut(application.subjectProperty.isPlanningOnRenting ? application.subjectProperty.isPlanningOnRenting : false));
                                dispatch(setSeparateUnit(application.subjectProperty.isSeparateLegalUnit ? application.subjectProperty.isSeparateLegalUnit : false));
                                dispatch(setMonthlyRent(application.subjectProperty.rent ? application.subjectProperty.rent : 0));
                                dispatch(setOwnOtherRentalProperties(application.subjectProperty.hasCurrentRentalProperties ? application.subjectProperty.hasCurrentRentalProperties : false));
                                dispatch(setSubjectPropertyRentalUnits(application.subjectProperty.subjectPropertyRentalUnits ? application.subjectProperty.subjectPropertyRentalUnits : 0));
                                dispatch(setNumOfRentalProperties(application.subjectProperty.totalRentalProperties ? application.subjectProperty.totalRentalProperties : 0));
                                dispatch(setNumOfRentalUnits(application.subjectProperty.totalRentalUnits ? application.subjectProperty.totalRentalUnits : 0));
                                dispatch(setOwnershipPercentage(application.subjectProperty.ownershipPercentage ? application.subjectProperty.ownershipPercentage : undefined));
                                if (application.subjectProperty.closingDate) {
                                    let closingDateJs = application.subjectProperty.closingDate;
                                    let closingMonth = (closingDateJs.month() + 1).toString();
                                    let dateString =
                                        `${closingDateJs.year()}-${closingMonth.length > 1 ? closingMonth : "0" + closingMonth}-` +
                                        `${closingDateJs.date().toString().length > 1 ? closingDateJs.date() : "0" + closingDateJs.date()}`

                                    if (dateString == "1901-01-01") {
                                        //Fetch locally saved closing date
                                        dateString = await fetchLocalDates(1);
                                    }
                                    dispatch(setClosingDate(dateString));
                                }
                                if (application.subjectProperty.estimatedFundingDate) {
                                    let fundingDateJs = application.subjectProperty.estimatedFundingDate;
                                    let fundingMonth = (fundingDateJs.month() + 1).toString();
                                    let dateString =
                                        `${fundingDateJs.year()}-${fundingMonth.length > 1 ? fundingMonth : "0" + fundingMonth}-` +
                                        `${fundingDateJs.date().toString().length > 1 ? fundingDateJs.date() : "0" + fundingDateJs.date()}`
                                    if (dateString == "1901-01-01") {
                                        //Fetch locally saved funding date
                                        dateString = await fetchLocalDates(2);
                                    }
                                    dispatch(setFundingDate(dateString));
                                }
                                if (application.subjectProperty.conditionOfFinancingDate) {
                                    let conditionDateJs = application.subjectProperty.conditionOfFinancingDate;
                                    let conditionMonth = (conditionDateJs.month() + 1).toString();
                                    let dateString =
                                        `${conditionDateJs.year()}-${conditionMonth.length > 1 ? conditionMonth : "0" + conditionMonth}-` +
                                        `${conditionDateJs.date().toString().length > 1 ? conditionDateJs.date() : "0" + conditionDateJs.date()}`
                                    dispatch(setFinancingConditionExists("yes"));
                                    dispatch(setFinancingConditionDate(dateString));
                                }

                                if (application.subjectProperty.address && application.subjectProperty.address.streetNumber != null) {
                                    dispatch(setPAStreetNumber(application.subjectProperty.address.streetNumber ? application.subjectProperty.address.streetNumber : ""));
                                    dispatch(setPAStreetName(application.subjectProperty.address.streetName ? application.subjectProperty.address.streetName : ""));
                                    dispatch(setPAStreetType(application.subjectProperty.address.streetType ? application.subjectProperty.address.streetType : ""));
                                    dispatch(setPAStreetDirection(application.subjectProperty.address.streetDirection ? application.subjectProperty.address.streetDirection : ""));
                                    dispatch(setPAUnitNumber(application.subjectProperty.address.unitNumber ? application.subjectProperty.address.unitNumber : ""));
                                    dispatch(setPAUnitType(application.subjectProperty.address.unitType ? application.subjectProperty.address.unitType : ""));
                                    dispatch(setPACity(application.subjectProperty.address.city));
                                    dispatch(setPAProvince(application.subjectProperty.address.region ? application.subjectProperty.address.region : ""));
                                    dispatch(setPAPostalCode(application.subjectProperty.address.postalCode ? application.subjectProperty.address.postalCode : ""));
                                    dispatch(setPACountry(application.subjectProperty.address.country));
                                }
                            }
                            fetchCoApplicants(application.dealId ? application.dealId : 0);
                            fetchAlloySession(application.dealId ? application.dealId : 0);
                        } else {
                            fetchCoApplicants(0);
                        }
                        setFetchingApplicationStatus(false);
                    }).catch((error) => {
                        navigate("/error/3");
                    });
                }

                fetchEmploymentData();

                //Fetch Assets
                client.apiAssetsGet().then(async assets => {
                    dispatch(clearAssetState());
                    let displayId = 1;
                    let cachedAssets: assetInfo[] = [];
                    if (assets && assets.nonRegistered) {
                        assets.nonRegistered.forEach(asset => {
                            let newAsset: assetInfo = {
                                displayId: displayId.toString(),
                                id: asset.id ? asset.id : 0,
                                visible: false,
                                assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                                assetType: asset.assetType_Name ? asset.assetType_Name : "",
                                origin: asset.origin_Name ? asset.origin_Name : "",
                                value: asset.value ? asset.value : 0,
                                valueText: asset.value != undefined ? asset.value.toString() : "",
                                description: asset.description ? asset.description : "",
                                downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                                downPaymentValueText: asset.downPaymentValue != undefined ? asset.downPaymentValue.toString() : "",
                                applicants: asset.applicants ? asset.applicants : [],
                                details: {
                                    nonRegistered: {
                                        financialInstitution: asset.fiName ? asset.fiName : "",
                                        otherFinancialInstitution: asset.fiNameOther ? asset.fiNameOther : "",
                                    },
                                    registered: undefined,
                                    realEstate: undefined,
                                    vehicle: undefined,
                                    lifeInsurance: undefined
                                }
                            }
                            dispatch(addAsset(newAsset));
                            cachedAssets.push(newAsset);
                            displayId++;
                        });
                    }
                    if (assets && assets.registered) {
                        assets.registered.forEach(asset => {
                            let newAsset: assetInfo = {
                                displayId: displayId.toString(),
                                id: asset.id ? asset.id : 0,
                                visible: false,
                                assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                                assetType: asset.assetType_Name ? asset.assetType_Name : "",
                                origin: asset.origin_Name ? asset.origin_Name : "",
                                value: asset.value ? asset.value : 0,
                                valueText: asset.value != undefined ? asset.value.toString() : "",
                                description: asset.description ? asset.description : "",
                                downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                                downPaymentValueText: asset.downPaymentValue != undefined ? asset.downPaymentValue.toString() : "",
                                applicants: asset.applicants ? asset.applicants : [],
                                details: {
                                    nonRegistered: undefined,
                                    registered: {
                                        financialInstitution: asset.fiName ? asset.fiName : "",
                                        otherFinancialInstitution: asset.fiNameOther ? asset.fiNameOther : "",
                                    },
                                    realEstate: undefined,
                                    vehicle: undefined,
                                    lifeInsurance: undefined
                                }
                            }
                            dispatch(addAsset(newAsset));
                            cachedAssets.push(newAsset);
                            displayId++;
                        });
                    }
                    if (assets && assets.realEstate) {
                        const assetExpenseIDs: Record<number, number> = {};
                        let expenseList: expenseInfo[] = [];
                        await client.apiExpensesGet().then(expenses => {
                            dispatch(clearExpenseState());
                            if (expenses && expenses.property) {
                                expenses.property.forEach(expense => {
                                    assetExpenseIDs[expense.assetId ? expense.assetId : 0] = expense.id ? expense.id : 0;
                                    let newExpense: expenseInfo = {
                                        displayId: (Object.keys(assetExpenseIDs).length).toString(),
                                        id: expense.id ? expense.id : 0,
                                        assetId: expense.assetId ? expense.assetId : 0,
                                        expenseCategory: "property",
                                        propertyTax: expense.taxAmount ? expense.taxAmount : 0,
                                        heatingCost: expense.utilityFeesAmount ? expense.utilityFeesAmount : 0,
                                        maintenanceFee: expense.maintenanceAmount ? expense.maintenanceAmount : 0,
                                        ownershipPercentage: expense.ownershipPercentage ? expense.ownershipPercentage : 0,
                                        applicants: expense.applicants ? expense.applicants : [],
                                    };
                                    dispatch(addExpense(newExpense));
                                    expenseList.push(newExpense);
                                });
                            }
                        }).catch((error) => {
                            console.log("Error fetching expenses", error);
                        });
                        let ownHome = false;
                        assets.realEstate.forEach(asset => {
                            const selectedExpense = expenseList.find(exp => exp.assetId === asset.id);
                            let originalPurchaseDateString = "";
                            let soldDate = "";
                            if (asset.origPurchasePrice) {
                                originalPurchaseDateString = asset.origPurchaseDate ? asset.origPurchaseDate.format("DD/MM/YYYY") : ""
                            }
                            if (asset.soldDate) {
                                soldDate = asset.soldDate ? asset.soldDate.format("DD/MM/YYYY") : ""
                            }

                            //Check if this real estate asset is their current address (w/ 'own' status)
                            ownHome = homeAddressRef.current?.address.streetNumber == asset.assetAddress_StreetNumber &&
                                homeAddressRef.current?.address.streetName == asset.assetAddress_StreetName &&
                                homeAddressRef.current?.address.city == asset.assetAddress_City;

                            let newAsset: assetInfo = {
                                displayId: displayId.toString(),
                                id: asset.id ? asset.id : 0,
                                visible: false,
                                assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                                assetType: asset.assetType_Name ? asset.assetType_Name : "",
                                origin: asset.origin_Name ? asset.origin_Name : "",
                                value: asset.value ? asset.value : 0,
                                valueText: asset.value != undefined ? asset.value.toString() : "",
                                description: asset.description ? asset.description : "",
                                downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                                downPaymentValueText: asset.downPaymentValue != undefined ? asset.downPaymentValue.toString() : "",
                                applicants: asset.applicants ? asset.applicants : [],
                                details: {
                                    nonRegistered: undefined,
                                    registered: undefined,
                                    realEstate: {
                                        occupancyType: asset.occupancyType_Name ? asset.occupancyType_Name : "",
                                        propertyType: asset.propertyType_Name ? asset.propertyType_Name : "",
                                        rentalComponent: asset.rentalComponent ? "yes" : "no",
                                        numOfProperties: asset.numberOfProperties ? asset.numberOfProperties.toString() : "",
                                        ownershipPercentage: selectedExpense ? selectedExpense.ownershipPercentage ? selectedExpense.ownershipPercentage.toString() : "" : "",
                                        address: {
                                            displayId: "1",
                                            streetNumber: asset.assetAddress_StreetNumber ? asset.assetAddress_StreetNumber : "",
                                            streetName: asset.assetAddress_StreetName ? asset.assetAddress_StreetName : "",
                                            streetType: asset.assetAddress_StreetType ? asset.assetAddress_StreetType : "",
                                            streetDirection: asset.assetAddress_StreetDirection ? asset.assetAddress_StreetDirection : "",
                                            unitType: asset.assetAddress_UnitType ? asset.assetAddress_UnitType : "",
                                            unitNumber: asset.assetAddress_UnitNumber ? asset.assetAddress_UnitNumber : "",
                                            city: asset.assetAddress_City ? asset.assetAddress_City : "",
                                            province: asset.assetAddress_Region ? asset.assetAddress_Region : "",
                                            postalCode: asset.assetAddress_PostalCode ? asset.assetAddress_PostalCode : "",
                                            country: asset.assetAddress_Country ? asset.assetAddress_Country : "",
                                        },
                                        originalPurchasePrice: asset.origPurchasePrice ? asset.origPurchasePrice : 0,
                                        originalPurchasePriceText: asset.origPurchasePrice ? asset.origPurchasePrice.toString() : "",
                                        originalPurchaseDate: originalPurchaseDateString,
                                        propertyTax: selectedExpense ? selectedExpense.propertyTax : 0,
                                        propertyTaxText: selectedExpense ? selectedExpense.propertyTax.toString() : "",
                                        heatingCost: selectedExpense ? selectedExpense.heatingCost : 0,
                                        heatingCostText: selectedExpense ? selectedExpense.heatingCost.toString() : "",
                                        maintenanceFee: selectedExpense ? selectedExpense.maintenanceFee : 0,
                                        maintenanceFeeText: selectedExpense ? selectedExpense.maintenanceFee.toString() : "",
                                        lot: asset.lot ? asset.lot : "",
                                        block: asset.block ? asset.block : "",
                                        plan: asset.plan ? asset.plan : "",
                                        section: asset.section ? asset.section : "",
                                        futureUsage: asset.futureUsageType_Name ? asset.futureUsageType_Name : "",
                                        hasPropertyBeenSold: asset.hasPropertyBeenSold ? "yes" : "no",
                                        soldDate: soldDate,
                                        rentalIncome: asset.rentalIncome ? asset.rentalIncome : 0,
                                        rentalIncomeText: asset.rentalIncome ? asset.rentalIncome.toString() : "",
                                        manualAddressEntered: false,
                                        isCurrentResidence: ownHome
                                    },
                                    vehicle: undefined,
                                    lifeInsurance: undefined
                                }
                            }
                            dispatch(addAsset(newAsset));
                            cachedAssets.push(newAsset);
                            displayId++;
                        });
                    }
                    if (assets && assets.vehicle) {
                        assets.vehicle.forEach(asset => {
                            let newAsset: assetInfo = {
                                displayId: displayId.toString(),
                                id: asset.id ? asset.id : 0,
                                visible: false,
                                assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                                assetType: asset.assetType_Name ? asset.assetType_Name : "",
                                origin: asset.origin_Name ? asset.origin_Name : "",
                                value: asset.value ? asset.value : 0,
                                valueText: asset.value != undefined ? asset.value.toString() : "",
                                description: asset.description ? asset.description : "",
                                downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                                downPaymentValueText: asset.downPaymentValue != undefined ? asset.downPaymentValue.toString() : "",
                                applicants: asset.applicants ? asset.applicants : [],
                                details: {
                                    nonRegistered: undefined,
                                    registered: undefined,
                                    realEstate: undefined,
                                    vehicle: {
                                        make: asset.make ? asset.make : "",
                                        model: asset.model ? asset.model : "",
                                        year: asset.year ? asset.year.toString() : "",
                                        ownership: asset.vehicleOwnershipType_Name ? asset.vehicleOwnershipType_Name : "",
                                    },
                                    lifeInsurance: undefined
                                }
                            };
                            dispatch(addAsset(newAsset));
                            cachedAssets.push(newAsset);
                            displayId++;
                        });
                    }
                    if (assets && assets.lifeInsurance) {
                        assets.lifeInsurance.forEach(asset => {
                            let newAsset: assetInfo = {
                                displayId: displayId.toString(),
                                id: asset.id ? asset.id : 0,
                                visible: false,
                                assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                                assetType: asset.assetType_Name ? asset.assetType_Name : "",
                                origin: asset.origin_Name ? asset.origin_Name : "",
                                value: asset.value ? asset.value : 0,
                                valueText: asset.value != undefined ? asset.value.toString() : "",
                                description: asset.description ? asset.description : "",
                                downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                                downPaymentValueText: asset.downPaymentValue != undefined ? asset.downPaymentValue.toString() : "",
                                applicants: asset.applicants ? asset.applicants : [],
                                details: {
                                    nonRegistered: undefined,
                                    registered: undefined,
                                    realEstate: undefined,
                                    vehicle: undefined,
                                    lifeInsurance: {
                                        cashSurrenderValue: asset.cashSurrenderValue ? asset.cashSurrenderValue : 0,
                                        cashSurrenderValueText: asset.cashSurrenderValue ? asset.cashSurrenderValue.toString() : "",
                                    }
                                }
                            };
                            dispatch(addAsset(newAsset));
                            displayId++;
                        });
                    }
                    if (assets && assets.other) {
                        assets.other.forEach(asset => {
                            let newAsset: assetInfo = {
                                displayId: displayId.toString(),
                                id: asset.id ? asset.id : 0,
                                visible: false,
                                assetCategory: asset.assetCategoryType_Name ? asset.assetCategoryType_Name : "",
                                assetType: asset.assetType_Name ? asset.assetType_Name : "",
                                origin: asset.origin_Name ? asset.origin_Name : "",
                                value: asset.value ? asset.value : 0,
                                valueText: asset.value != undefined ? asset.value.toString() : "",
                                description: asset.description ? asset.description : "",
                                downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                                downPaymentValueText: asset.downPaymentValue != undefined ? asset.downPaymentValue.toString() : "",
                                applicants: asset.applicants ? asset.applicants : [],
                                details: {
                                    nonRegistered: undefined,
                                    registered: undefined,
                                    realEstate: undefined,
                                    vehicle: undefined,
                                    lifeInsurance: undefined
                                }
                            };
                            dispatch(addAsset(newAsset));
                            cachedAssets.push(newAsset);
                            displayId++;
                        });
                    }
                    if (assets && assets.downPayment) {
                        assets.downPayment.forEach(asset => {
                            let newAsset: assetInfo = {
                                displayId: displayId.toString(),
                                id: asset.id ? asset.id : 0,
                                visible: false,
                                assetCategory: asset.assetType_Name ? asset.assetType_Name : "",
                                assetType: asset.assetType_Name ? asset.assetType_Name : "",
                                origin: asset.origin_Name ? asset.origin_Name : "",
                                value: asset.downPaymentValue ? asset.downPaymentValue : 0,
                                valueText: asset.downPaymentValue != undefined ? asset.downPaymentValue.toString() : "",
                                description: asset.description ? asset.description : "",
                                downPaymentValue: asset.downPaymentValue ? asset.downPaymentValue : 0,
                                downPaymentValueText: asset.downPaymentValue != undefined ? asset.downPaymentValue.toString() : "",
                                applicants: asset.applicants ? asset.applicants : [],
                                details: {
                                    nonRegistered: undefined,
                                    registered: undefined,
                                    realEstate: undefined,
                                    vehicle: undefined,
                                    lifeInsurance: undefined
                                }
                            };
                            dispatch(addAsset(newAsset));
                            cachedAssets.push(newAsset);
                            displayId++;
                        });
                    }
                    setAssetsFetched(true);
                });

                client.apiDealsApplicantConsentGet().then(consent => {
                    if (consent && consent.date) {
                        dispatch(setConsentCompleted(true));
                    }
                }).catch((error) => {
                    //navigate("/error/3");
                });
            } else {
                //If this user has no deal (i.e. id is zero) and there is an existing one cached in the browser (alongside the joint code), 
                // then automatically update the user to use this deal and proceed with the application
                if (application && application.id != 0) {

                    //Since they've joined the deal as a co-applicant,update the Alloy session ID
                    let newAlloySessionId = generateSessionId(true);
                    dispatch(setAlloySessionId(newAlloySessionId));

                    client.apiDealsJoin(new GetDealById({
                        dealId: application.id
                    })).then((result) => {
                        let localInfo: any = {}
                        let updatedApplication = {
                            ...application,
                            isCoApplicant: true,
                            alloyApplicationToken: "",
                            status: "",
                            applicationEndpointReached: false,
                            idVerificationSubmitted: false,
                            underStaffReview: false
                        };
                        let updatedApplicant = {
                            ...applicant,
                            alloySessionId: newAlloySessionId,
                        };
                        localInfo.applicant = updatedApplicant;
                        localInfo.application = updatedApplication;

                        client.apiIdverificationInit(new InitIdVerificationDto({
                            alloySessionId: newAlloySessionId,
                            applicationId: 0, //This will be updated once the deal is created on Trellis
                            applicantId: applicant.id,
                            status: "PENDING",
                            phoneVerified: applicant.twilioResult.codeVerified
                        })).then(function (data) {
                            client.apiApplicationsSave(new ApplicationDto({
                                applicationData: JSON.stringify(localInfo)
                            })).then((result) => {
                                dispatch(setIsCoApplicant(true));

                            }).catch((error) => {
                                console.log("Error saving application info");
                            });

                        }).catch((error) => {
                            console.log(error);

                        });
                    }).catch((error) => {
                        console.log("Error joining deal with joint code");
                    });
                } else if (referralProducts != undefined && referralProducts.length > 0) {
                    //If there is a product attached to the deal, assume the user is a primary
                    dispatch(setIsCoApplicant(false));
                }
                setFetchingApplicationStatus(false);
            }
        }).catch((error) => {
            console.log(error);
            setFetchingApplicationStatus(false);
            navigate("/error/3");
        });
    }

    const fetchCoApplicants = (dealId: number) => {
        if (dealId == 0) return;
        client.apiApplicationsFetch().then((result) => {
            if (result && result.coapplicants) {
                fetchCoApplicantData(dealId, result.coapplicants);
            }
        }).catch((error) => {
            console.log("Error saving applicant info");
        });
    }

    const fetchCoApplicantData = (dealId: number, existingCoapplicants: coapplicant[]) => {
        if (dealId == 0) {
            //For Existing members their member number will be used instead of the email address
            client.apiCoapplicantsFilter(credential.email).then(coapplicants => {
                dispatch(clearCoApplicantState());
                let id = 1;
                if (coapplicants != undefined && coapplicants.length > 0) {
                    coapplicants.forEach(coapplicant => {

                        existingCoapplicants.forEach(existingCoapplicant => {
                            if (coapplicant.inviteCode == existingCoapplicant.inviteCode) {
                                let month = "";
                                if (coapplicant.dateOfBirth && coapplicant.dateOfBirth.month() < 9) {
                                    month = "0" + (coapplicant.dateOfBirth.month() + 1).toString();
                                } else {
                                    month = (coapplicant.dateOfBirth ? coapplicant.dateOfBirth.month() + 1 : "").toString();
                                }
                                let newCoapplicant: coapplicant = {
                                    id: id + "",
                                    applicantId: coapplicant.applicantId ? coapplicant.applicantId : 0,
                                    firstName: coapplicant.firstName ? coapplicant.firstName : "",
                                    lastName: coapplicant.lastName ? coapplicant.lastName : "",
                                    dob_year: existingCoapplicant.dob_year,
                                    dob_month: existingCoapplicant.dob_month,
                                    dob_day: existingCoapplicant.dob_day,
                                    phoneNumber: coapplicant.phoneNumber ? coapplicant.phoneNumber : "",
                                    relationship: coapplicant.relationship ? coapplicant.relationship : "",
                                    email: coapplicant.emailAddress ? coapplicant.emailAddress : "",
                                    inviteCode: coapplicant.inviteCode ? coapplicant.inviteCode : "",
                                    isInviteActive: coapplicant.isActiveLink != undefined ? coapplicant.isActiveLink : true
                                }
                                dispatch(addCoApplicant(newCoapplicant));
                                id++;
                            }
                        });
                    });
                }
            }).catch((error) => {
                console.log(error)
            });
        } else {
            client.apiCoapplicantsList().then(coapplicants => {
                dispatch(clearCoApplicantState());
                let id = 1;
                if (coapplicants != undefined && coapplicants.length > 0) {
                    coapplicants.forEach(coapplicant => {
                        existingCoapplicants.forEach(existingCoapplicant => {
                            if (coapplicant.inviteCode == existingCoapplicant.inviteCode) {
                                let month = "";
                                if (coapplicant.dateOfBirth && coapplicant.dateOfBirth.month() < 9) {
                                    month = "0" + (coapplicant.dateOfBirth.month() + 1).toString();
                                } else {
                                    month = (coapplicant.dateOfBirth ? coapplicant.dateOfBirth.month() + 1 : "").toString();
                                }
                                let newCoapplicant: coapplicant = {
                                    id: id + "",
                                    applicantId: coapplicant.applicantId ? coapplicant.applicantId : 0,
                                    firstName: coapplicant.firstName ? coapplicant.firstName : "",
                                    lastName: coapplicant.lastName ? coapplicant.lastName : "",
                                    dob_year: existingCoapplicant.dob_year,
                                    dob_month: existingCoapplicant.dob_month,
                                    dob_day: existingCoapplicant.dob_day,
                                    phoneNumber: coapplicant.phoneNumber ? coapplicant.phoneNumber : "",
                                    relationship: coapplicant.relationship ? coapplicant.relationship : "",
                                    email: coapplicant.emailAddress ? coapplicant.emailAddress : "",
                                    inviteCode: coapplicant.inviteCode ? coapplicant.inviteCode : "",
                                    isInviteActive: coapplicant.isActiveLink != undefined ? coapplicant.isActiveLink : true
                                }
                                dispatch(addCoApplicant(newCoapplicant));
                                id++;
                            }
                        });
                    });
                }
            }).catch((error) => {
                console.log(error)
            });
        }
    }

    const fetchAlloySession = (dealId: number) => {
        //Fetch the alloy session id that exists in IDV table (Only fetch local ID if deal has not been established)
        if (dealId != 0) {
            client.apiIdverificationIdverificationapplicant().then((result) => {
                if (result && result.alloySessionId && result.alloySessionId != "") {
                    dispatch(setAlloySessionId(result.alloySessionId));
                } else {
                    if (applicant && applicant.alloySessionId == "") {
                        dispatch(setAlloySessionId(generateSessionId(application.isCoApplicant)));
                    }
                }
            });
        }
    }

    const fetchLocalDates = async (type: number): Promise<string> => {
        // Fetch locally saved dates
        try {
            const appData = await client.apiApplicationsFetch();
            if (appData && appData.propertyInfo) {
                if (type == 1) {
                    return appData.propertyInfo.closingDate ? appData.propertyInfo.closingDate : "1901-01-01"; // Return a default date if none exists
                } else {
                    return appData.propertyInfo.fundingDate ? appData.propertyInfo.fundingDate : "1901-01-01";
                }
            } else {
                return "1901-01-01"; // Return a default date if error occurs
            }
        } catch (error) {
            console.log("Error fetching application info", error);
            return "1901-01-01"; // Return a default date if error occurs
        }
    }

    useEffect(() => {
        if (application && application.applicationEndpointReached) {
            dispatch(setCredentialsComplete(true));
            dispatch(setMortgageComplete(true));
            dispatch(setProfileComplete(true));
            dispatch(setCoapplicantComplete(true));
            dispatch(setHomeDetailsComplete(true));
            dispatch(setFinalizeAccoutComplete(true));

            if (application.underStaffReview) {
                client.apiApplicationsFetch().then((result) => {
                    if (result != undefined) {
                        result.application = application;
                        client.apiApplicationsSave(new ApplicationDto({
                            applicationData: JSON.stringify(result)
                        })).catch((error) => {
                            console.log("Error saving applicant info");
                        });
                    }
                }).catch((error) => {
                    console.log("Error saving applicant info");
                });
            }
        }

        if (application && application.status == "Cancelled") {
            client.apiApplicationsFetch().then((result) => {
                if (result == null) {
                    let localInfo: any = {};
                    localInfo.application = application;
                    localInfo.applicant = applicant;
                    client.apiApplicationsSave(new ApplicationDto({
                        applicationData: JSON.stringify(localInfo)
                    })).catch((error) => {
                        console.log("Error saving applicant info");
                    });
                } else {
                    result.application = application;
                    client.apiApplicationsSave(new ApplicationDto({
                        applicationData: JSON.stringify(result)
                    })).catch((error) => {
                        console.log("Error saving applicant info");
                    });
                }
            }).catch((error) => {
                console.log("Error saving applicant info");
            });

            //If the user is a co-applicant and the current deal is cancelled, automatically detach from this deal
            if (application.isCoApplicant && !dealDetached.current) {
                detachFromDeal();
                dealDetached.current = true;
            }
        }
    }, [application]);

    useEffect(() => {
        if (applicant && application && application.id != 0 && applicant.idVerificationStatus == verificationCategory.Pass
            && !application.underStaffReview && application.applicationEndpointReached) {
            setSingleClickInsightEvent("IDVerificationComplete", "pass")
            setClickInsightEvents(referralProducts, "applicationFlowComplete", "main", "new", "under-review", application.id, applicant);
            setApplicationDialogVisible(true);
        }
    }, [applicant, application]);

    useEffect(() => {
        if (applicant && application && assetsFetched) {
            let ownHome = false;
            assets.forEach(asset => {
                if (asset.details.realEstate?.isCurrentResidence) {
                    ownHome = true;
                }
            });
            if (!ownHome && applicant.residentStatus == "own" && application.applicationCategory == applicationCategory.Buy) {
                let assetId = (assets.length + 1).toString();

                //Auto hide other assets (if they're visible)
                assets.forEach(a => {
                    if (a.displayId != assetId) {
                        let newAssetInfo = { ...a }
                        newAssetInfo.visible = false;
                        dispatch(setAssetInfo(newAssetInfo));
                    }
                });

                let newAssetInfo: assetInfo = {
                    displayId: assetId,
                    id: 0,
                    visible: true,
                    assetCategory: "realestate",
                    assetType: "",
                    origin: "",
                    value: 0,
                    valueText: "",
                    description: "",
                    downPaymentValue: 0,
                    downPaymentValueText: "",
                    applicants: [applicant.id],
                    details: {
                        nonRegistered: undefined,
                        registered: undefined,
                        realEstate: {
                            occupancyType: "primary",
                            propertyType: "",
                            rentalComponent: "",
                            numOfProperties: "",
                            ownershipPercentage: "",
                            address: {
                                displayId: assetId,
                                streetNumber: homeAddressRef.current?.address.streetNumber ? homeAddressRef.current.address.streetNumber : "",
                                streetName: homeAddressRef.current?.address.streetName ? homeAddressRef.current.address.streetName : "",
                                streetType: homeAddressRef.current?.address.streetType ? homeAddressRef.current.address.streetType : "",
                                streetDirection: homeAddressRef.current?.address.streetDirection ? homeAddressRef.current.address.streetDirection : "",
                                unitType: homeAddressRef.current?.address.unitType ? homeAddressRef.current.address.unitType : "",
                                unitNumber: homeAddressRef.current?.address.unitNumber ? homeAddressRef.current.address.unitNumber : "",
                                city: homeAddressRef.current?.address.city ? homeAddressRef.current.address.city : "",
                                province: homeAddressRef.current?.address.province ? homeAddressRef.current.address.province : "",
                                postalCode: homeAddressRef.current?.address.postalCode ? homeAddressRef.current.address.postalCode : "",
                                country: homeAddressRef.current?.address.country ? homeAddressRef.current.address.country : ""
                            },
                            originalPurchasePrice: 0,
                            originalPurchasePriceText: "",
                            originalPurchaseDate: "",
                            propertyTax: 0,
                            propertyTaxText: "",
                            heatingCost: 0,
                            heatingCostText: "",
                            maintenanceFee: 0,
                            maintenanceFeeText: "",
                            lot: "",
                            block: "",
                            plan: "",
                            section: "",
                            futureUsage: "",
                            hasPropertyBeenSold: "",
                            soldDate: "",
                            rentalIncome: 0,
                            rentalIncomeText: "",
                            manualAddressEntered: false,
                            isCurrentResidence: true
                        },
                        vehicle: undefined,
                        lifeInsurance: undefined
                    }
                };
                dispatch(addAsset(newAssetInfo));
            }
        }
    }, [applicant, homeAddressRef, application, assetsFetched]);

    useEffect(() => {
        dispatch(setCachedAssets(assets));
        dispatch(setCachedExpenses(expenses));
    }, [assets, expenses]);

    const resumeApplication = () => {
        dispatch(setCredentialsComplete(true));
        console.log(application);
        if (!application.isCoApplicant && application.id == 0) {
            navigate("/single/select-application");
            if (application.applicationProgress == 0) {
                return;
            }
            if (application.applicationCategory == applicationCategory.Buy) {
                navigate("/single/select-assessment");
                if (application.applicationProgress == 1) {
                    return;
                }
                navigate("/single/purchase-price");
                if (application.applicationProgress == 2) {
                    return;
                }
                navigate("/single/down-payment");
                if (application.applicationProgress == 3) {
                    return;
                }
            } else {
                navigate("/single/property-value");
                if (application.applicationProgress == 1) {
                    return;
                }
                navigate("/single/mortgage-balance");
                if (application.applicationProgress == 3) {
                    return;
                }
            }

            navigate("/single/mortgage-term");
            if (application.applicationProgress == 4) {
                return;
            }
            navigate("/single/amortization");
            if (application.applicationProgress == 5) {
                return;
            }
            if (application.applicationCategory == applicationCategory.Buy) {
                navigate("/single/closing-date");
                if (application.applicationProgress == 6) {
                    return;
                }
            } else {
                navigate("/single/funding-date");
                if (application.applicationProgress == 6) {
                    return;
                }
            }
        }
        dispatch(setMortgageComplete(true));
        if (application.isCoApplicant) {
            navigate("/joint/mortgage-details")
            if (application.applicationProgress == 0) {
                return;
            }
        }

        dispatch(setCoapplicantComplete(true));
        application.isCoApplicant ? navigate("/joint/personal-info") : navigate("/single/personal-info");
        if (application.applicationProgress == 7) {
            return;
        }
        if (!application.isCoApplicant) {
            navigate("/single/co-applicant");
            if (application.applicationProgress == 8) {
                return;
            }
        }
        application.isCoApplicant ? navigate("/joint/address") : navigate("/single/address");
        if ((application.applicationProgress == 9 && !application.isCoApplicant) || (application.isCoApplicant && application.applicationProgress == 8)) {
            return;
        }

        //Navigate only if minimum address history is not satisfied
        if ((applicant.residenceYears * 12) + applicant.residenceMonths < 36) {
            application.isCoApplicant ? navigate("/joint/previous-address") : navigate("/single/previousaddress");
            if (application.applicationProgress == 9) {
                return;
            }
        }
        application.isCoApplicant ? navigate("/joint/employment") : navigate("/single/employment");
        if (application.applicationProgress == 10) {
            return;
        }

        //Navigate only if minimum employment history is not satisfied
        let totalEmploymentTime = 0;
        currentEmployment.forEach(c => {
            totalEmploymentTime += (c.employmentYears ? (c.employmentYears * 12) : 0) + (c.employmentMonths ? c.employmentMonths : 0);
            totalEmploymentTime += (c.notWorkingYears ? (c.notWorkingYears * 12) : 0) + (c.notWorkingMonths ? c.notWorkingMonths : 0);
        })
        if (totalEmploymentTime < 36) {
            application.isCoApplicant ? navigate("/joint/previous-employment") : navigate("/single/previous-employment");
            if ((application.applicationProgress == 11 && !application.isCoApplicant) || (application.isCoApplicant && application.applicationProgress == 12)) {
                return;
            }
        }
        dispatch(setProfileComplete(true));
        if (!application.isCoApplicant) {
            if (application.applicationCategory === applicationCategory.Buy) {
                navigate("/single/assets");
            }
            if (application.applicationProgress == 12) {
                return;
            }
            if (application.applicationCategory !== applicationCategory.Buy) {
                navigate("/single/refi-property-details");
            } else {
                navigate("/single/property-details");
            }
            if (application.applicationProgress == 13) {
                return;
            }
        } else {
            navigate("/joint/property-details");
            if (application.applicationProgress == 13) {
                return;
            }
        }
        if (!application.isCoApplicant) {
            navigate("/single/property-purpose");
            if (application.applicationProgress == 14) {
                return;
            }
            navigate("/single/financing-condition");
            if (application.applicationProgress == 15) {
                return;
            }
            dispatch(setHomeDetailsComplete(true));
            navigate("/single/review-application");
            if (application.applicationProgress == 16) {
                return;
            }
            navigate("/single/legal-terms")
            if (application.applicationProgress == 17) {
                return;
            }
        } else {
            dispatch(setHomeDetailsComplete(true));
            navigate("/joint/review-application");
            if (application.applicationProgress == 14) {
                return;
            }
        }
    }

    const getDeviceOrientation = (): 'portrait' | 'landscape' => {
        if (typeof window === 'undefined') return 'portrait';
        if (window.screen?.orientation?.type) {
            return window.screen.orientation.type.startsWith('portrait') ? 'portrait' : 'landscape';
        }
        return window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape';
    };

    const getBrowserZoomPercent = (): number => {
        if (typeof window === "undefined") return 100;
        // Primary: devicePixelRatio (most browsers)
        if (window.devicePixelRatio) {
            return Math.round(window.devicePixelRatio * 100);
        }
        // Fallback: ratio of screen width to innerWidth
        const ratio = screen.width && window.innerWidth
            ? (screen.width / window.innerWidth)
            : 1;
        return Math.round(ratio * 100);
    };

    return (
        <FadeTransition>
            <main aria-labelledby="main-content">
                <div className="main-container max-md:mt-10">
                    <div className={style.emHeading}>
                        <div className="grid grid-cols-1" style={{ textAlign: "left" }}>
                            {applicant.firstName != "" ? <h1 style={{ color: "var(--brand-blue)" }}>Welcome Back, {applicant.firstName}.</h1> : <h1 style={{ color: "var(--brand-blue)" }}>Welcome Back!</h1>}
                            <h4 style={{ fontWeight: 'normal' }}>We've saved all your info so you can pick up where you left off.</h4>
                        </div>
                    </div>
                    <div style={{
                        position: "absolute", zIndex: 1, top:
                            (screen.height < 450 || (isMobileDevice() && getDeviceOrientation() == "landscape")) ? "45%" :
                                getBrowserZoomPercent() == 125 || getBrowserZoomPercent() == 150 ? "22%" :
                                    getBrowserZoomPercent() == 175 ? "28%" :
                                        getBrowserZoomPercent() == 200 ? "32%" :
                                            getBrowserZoomPercent() == 250 ? "50%" : "18%"
                    }} className={style.applicationProgress}>
                        <div className="flex md:flex-row md:max-w-[885px] flex-col">
                            <div className={clsx("flex flex-row", style.progressContainer)} style={{ background: "#ffffff", padding: 20, justifyContent: "space-between", borderRadius: 12 }}>
                                <div>
                                    {((application.status !== "Cancelled")
                                        || (application.jointCode != "" && application.id != 0) || (application.id != 0)) &&
                                        <div className="mb-5">
                                            <b style={{ fontSize: 20 }}>Your Loan Progress</b>
                                        </div>
                                    }

                                    {(fetchingApplicantStatus || fetchingApplicationStatus) &&
                                        <div className="justify-center flex">
                                            <FontAwesomeIcon icon={faSpinner} spin style={{ margin: 'auto' }} className={clsx("")} />
                                        </div>
                                    }
                                    {!fetchingApplicantStatus && !fetchingApplicationStatus &&
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0 }}>
                                            {
                                                ((application.status !== "Cancelled")
                                                    || (application.jointCode != "" && application.id != 0) || (application.id != 0)) ?
                                                    application.applicationEndpointReached ?
                                                        <div className="flex">
                                                            <img src="assets/completed.svg" className="mr-1" alt="complete" />
                                                            <b style={{ fontSize: 14, color: "#15892C" }} className="ml-2 mt-1">COMPLETE</b>
                                                        </div>
                                                        :
                                                        <div className="flex">
                                                            <img src="assets/in-progress.svg" className="mr-1" alt="complete" />
                                                            <b style={{ fontSize: 14, color: "#077A9C" }} className="ml-2 mt-1">CURRENT STEP</b>
                                                        </div>
                                                    :
                                                    <>
                                                    </>
                                            }
                                            {((application.status !== "Cancelled")
                                                || (application.jointCode != "" && application.id != 0) || (application.id != 0)) &&
                                                <>

                                                    <div className={clsx("ml-2", application.applicationEndpointReached ? style.verticalProgressBarComplete : style.verticalProgressBarOnGoing)}>
                                                        <div className="ml-6">
                                                            <b>Fill out an application</b>
                                                            <p>{((application.status !== "Cancelled")
                                                                || (application.jointCode != "" && application.id != 0) || (application.id != 0)) ?
                                                                "Tell us about yourself and the type of product you’re looking for."
                                                                : "Your current application is cancelled. Please start a new application to continue."}</p>
                                                            {!application.applicationEndpointReached && ((application.status !== "Cancelled")
                                                                || (application.jointCode != "" && application.id != 0) || (application.id != 0)) &&
                                                                <motion.button
                                                                    type="submit"
                                                                    className="primary mt-2 w-169 sm:w-28 px-6"
                                                                    onClick={completeRequiredItems}
                                                                    whileHover={{ scale: 1.05 }}
                                                                    style={{ width: 150, paddingLeft: 4, paddingRight: 4, height: "2rem" }}
                                                                >
                                                                    Complete Step
                                                                    <img
                                                                        src="assets/white-chevron-right.svg"
                                                                        className={clsx(
                                                                            "w-5 h-6 top-6 ml-"
                                                                        )}
                                                                        alt="error"
                                                                    />
                                                                </motion.button>
                                                            }
                                                        </div>
                                                    </div>

                                                    {applicant.isProspect && <>
                                                        {applicant.idVerificationStatus == verificationCategory.Pass || applicant.idVerificationStatus == verificationCategory.Hardfail ?
                                                            <div className="flex">
                                                                <img src="assets/completed.svg" className="mr-1" alt="complete" />
                                                                <b style={{ fontSize: 14, color: "#15892C" }} className="ml-2 mt-1">COMPLETE</b>
                                                            </div>
                                                            :
                                                            <>
                                                                {application.applicationEndpointReached ?
                                                                    <div className="flex">
                                                                        <img src="assets/in-progress.svg" className="mr-1" alt="complete" />
                                                                        <b style={{ fontSize: 14, color: "#077A9C" }} className="ml-2 mt-1">CURRENT STEP</b>
                                                                    </div>
                                                                    :
                                                                    <div className="flex">
                                                                        <img src="assets/progress-upcoming.svg" className="mr-1" alt="complete" />
                                                                        <b style={{ fontSize: 14, }} className="ml-2 mt-1">UPCOMING</b>
                                                                    </div>
                                                                }
                                                            </>
                                                        }
                                                    </>}
                                                    {applicant.isProspect && <div className={clsx("ml-2",
                                                        applicant.idVerificationStatus == verificationCategory.Pass
                                                            ? style.verticalProgressBarComplete : style.verticalProgressBarOnGoing)}>
                                                        <div className="ml-6">
                                                            <b>Verify your Identity</b>
                                                            {application.applicationEndpointReached && applicant.idVerificationStatus != verificationCategory.Hardfail &&
                                                                <span
                                                                    className="ml-4"
                                                                    style={{
                                                                        background:
                                                                            applicant.idVerificationStatus == verificationCategory.Pending || applicant.idVerificationStatus == verificationCategory.Processing ? "#EBF8FC" :
                                                                                applicant.idVerificationStatus == verificationCategory.Pass ? "#D8ECDC" :
                                                                                    applicant.idVerificationStatus == verificationCategory.Softfail ? "#FFF8E9" : "#FCF2F2",
                                                                        color:
                                                                            applicant.idVerificationStatus == verificationCategory.Pending || applicant.idVerificationStatus == verificationCategory.Processing ? "#005580" :
                                                                                applicant.idVerificationStatus == verificationCategory.Pass ? "#06560F" :
                                                                                    applicant.idVerificationStatus == verificationCategory.Softfail ? "#946A00" : "#910304",
                                                                        paddingLeft: 10,
                                                                        paddingRight: 10,
                                                                        paddingTop: 2,
                                                                        paddingBottom: 2,
                                                                        borderRadius: 12,
                                                                        fontSize: 14,
                                                                        fontWeight: "bold"
                                                                    }}>
                                                                    {applicant.idVerificationStatus == verificationCategory.Pending ? "NOT STARTED" :
                                                                        applicant.idVerificationStatus == verificationCategory.Processing ? "PROCESSING - CHECK BACK LATER" :
                                                                            applicant.idVerificationStatus == verificationCategory.Softfail ? "PENDING" :
                                                                                applicant.idVerificationStatus == verificationCategory.Pass ? "PASSED" : "FAILED"}
                                                                </span>
                                                            }
                                                            <p>Submit documents to help us verify the information you provided.</p>
                                                            {application.applicationEndpointReached && (applicant.idVerificationStatus == verificationCategory.Softfail
                                                                || applicant.idVerificationStatus == verificationCategory.Pending) && ((application.status !== "Cancelled")
                                                                    || (application.jointCode != "" && application.id != 0) || (application.id != 0)) &&
                                                                <motion.button
                                                                    type="submit"
                                                                    className="primary mt-2 w-169 sm:w-28 px-6"
                                                                    onClick={completeRequiredItems}
                                                                    whileHover={{ scale: 1.05 }}
                                                                    style={{
                                                                        width: 150,
                                                                        paddingLeft: 4, paddingRight: 4, height: "2rem"
                                                                    }}>
                                                                    Complete Step
                                                                    <img
                                                                        src="assets/white-chevron-right.svg"
                                                                        className={clsx(
                                                                            "w-5 h-6 top-6 ml-"
                                                                        )}
                                                                        alt="error"
                                                                    />
                                                                </motion.button>
                                                            }
                                                        </div>
                                                    </div>}

                                                    {application.applicationEndpointReached &&
                                                        legalTerms.legalTerms.fieldsCompleted && (applicant.idVerificationStatus == verificationCategory.Pass ||
                                                            applicant.idVerificationStatus == verificationCategory.Hardfail) ?
                                                        <div className="flex">
                                                            {(applicant.idVerificationStatus == verificationCategory.Pass) ?
                                                                <>
                                                                    <img src="assets/completed.svg" className="mr-1" alt="completed" />
                                                                    <b style={{ fontSize: 14, color: "#15892C" }} className="ml-2 mt-1">COMPLETE</b>
                                                                </>
                                                                :
                                                                <>
                                                                    <img src="assets/cancelled.svg" className="mr-1" alt="completed" />
                                                                    <b style={{ fontSize: 14, color: "#910304" }} className="ml-2 mt-1">DECLINED</b>
                                                                </>
                                                            }
                                                        </div>
                                                        :
                                                        <div className="flex">
                                                            <img src="assets/progress-upcoming.svg" className="mr-1" alt="upcoming" />
                                                            <b style={{ fontSize: 14, }} className="ml-2 mt-1">UPCOMING</b>
                                                        </div>
                                                    }
                                                    <div className={clsx("ml-2", style.verticalProgressBarFinal)}>
                                                        <div className="ml-6">
                                                            <b>Submitted for Review</b>
                                                            {applicant.idVerificationStatus == verificationCategory.Hardfail ?
                                                                <p>Your application was unsuccessful.</p> :
                                                                <p>One of our associates is currently reviewing your application and will be in touch shortly.</p>
                                                            }
                                                            {application.applicationEndpointReached && applicant.idVerificationStatus == verificationCategory.Hardfail &&
                                                                <motion.button
                                                                    type="submit"
                                                                    className="primary mt-2 w-169 sm:w-28 px-6"
                                                                    onClick={completeRequiredItems}
                                                                    whileHover={{ scale: 1.05 }}
                                                                    style={{
                                                                        width: 150,
                                                                        paddingLeft: 4, paddingRight: 4, height: "2rem"
                                                                    }}>
                                                                    Learn More
                                                                    <img
                                                                        src="assets/white-chevron-right.svg"
                                                                        className={clsx(
                                                                            "w-5 h-6 top-6 ml-"
                                                                        )}
                                                                        alt="error"
                                                                    />
                                                                </motion.button>
                                                            }
                                                        </div>
                                                    </div>
                                                </>
                                            }

                                            {/* Display the cancellation message under any of these conditions
                                             1. Application is Cancelled
                                             2. No Referral Products Selected (if signing in as a co-applicant after a previously cancelled deal)
                                             3. No invitation code present */}


                                            {(application.status === "Cancelled" && application.id == 0
                                                && (application.jointCode === "" || application.id === 0)) &&
                                                <div className="wrapper" aria-label="Application cancelled">
                                                    <div className="card" role="status" aria-live="polite">
                                                        <div className="icon" aria-hidden="true" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                                                            <img src="assets/info-error.svg" />
                                                        </div>

                                                        <b style={{ display: 'block', textAlign: 'center', width: '100%', fontSize: 24 }}>Application Cancelled</b>
                                                        <p className="subtitle" style={{ textAlign: 'center', width: '100%', paddingLeft: '10%', paddingRight: '10%', color: '#000000', fontWeight: '300' }}>
                                                            {application.isCoApplicant
                                                                ? "The application you were part of has been cancelled. "
                                                                : "Your application has been cancelled. You can start a new application at any time."}
                                                        </p>

                                                        <div className="panel" role="region" aria-label="Cancellation information" style={{ background: "#FAFAFA", padding: 20, borderRadius: 12, marginTop: 20 }}>
                                                            <div className="mt-2">
                                                                <b style={{ fontSize: 18, fontWeight: '600' }}>Why was the application cancelled?</b>
                                                                <p className="text" style={{ color: '#000000', fontWeight: '300' }}>Applications may be cancelled due to:</p>
                                                                <ul style={{ listStyle: "disc", paddingLeft: 20, marginTop: 8, fontWeight: '300' }}>
                                                                    <li>
                                                                        {application.isCoApplicant ? "A request for cancellation by the primary applicant" : "Request for cancellation"}
                                                                    </li>
                                                                    <li>Incomplete verification</li>
                                                                </ul>
                                                            </div>

                                                            <div className="mt-5">
                                                                <b style={{ fontSize: 18, fontWeight: '600' }}>Does this affect my eligibility?</b>
                                                                <p className="text" style={{ color: '#000000', fontWeight: '300' }}>No. Cancelling an application does not impact your ability to apply for credit or a mortgage in the future.</p>
                                                            </div>

                                                            <div className="mt-5">
                                                                <b style={{ fontSize: 18, fontWeight: '600' }}>Questions? We're here to help</b>
                                                                <p className="text" style={{ color: '#000000', fontWeight: '300' }}>
                                                                    {application.isCoApplicant ? "Please reach out to the primary applicant for details regarding this cancellation or to discuss next steps."
                                                                        : "If you have questions about this cancellation or need assistance, please contact our mortgage support team."}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {!application.isCoApplicant &&
                                                            <div className="mb-5">
                                                                <button
                                                                    type="submit"
                                                                    className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                                    style={{ width: '100%', fontWeight: 'normal' }}
                                                                    disabled={initiatingNewApplication}
                                                                    onClick={startNewApplication}
                                                                >
                                                                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(initiatingNewApplication ? "" : "hidden")} />
                                                                    Start New Application
                                                                </button>
                                                            </div>
                                                        }
                                                    </div>
                                                </div>
                                            }
                                        </motion.div>}
                                </div>
                            </div>
                            <motion.div
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0 }}
                                className="flex md:ml-4" style={{ background: "#ffffff", padding: 20, paddingRight: 30, justifyContent: "space-between", borderRadius: 12, height: "fit-content" }}>
                                <div>
                                    <div className="mb-5">
                                        <b style={{ fontSize: 20 }}>Contact Us</b>
                                    </div>
                                    <a className="flex brand-blue-text" href="tel:+18665922226" style={{ fontWeight: 'normal' }}><img
                                        src="assets/smartphone.svg"
                                        className={clsx(
                                            "w-5 h-6 top-6 mr-1"
                                        )}
                                        alt="error"
                                    />1-866.592.2226</a>
                                    <a className="flex brand-blue-text" href="mailto:mortgagesupport@meridiancu.ca" style={{ fontWeight: 'normal' }}><img
                                        src="assets/email.svg"
                                        className={clsx(
                                            "w-5 h-6 top-6 mr-1"
                                        )}
                                        alt="error"
                                    />mortgagesupport@meridiancu.ca</a>
                                    {/* <p className="flex brand-blue-text">
                                        <img
                                            src="assets/chat.svg"
                                            className={clsx(
                                                "w-5 h-6 top-6 mr-1"
                                            )}
                                            alt="error"
                                        />Secure Message</p> */}
                                </div>
                            </motion.div>
                        </div>
                    </div>
                    <div className="content-container">
                        <div className="blue-background" style={{ height: "25vh" }}></div>
                    </div>
                </div>
                <Dialog header={<h2 className="mt-5">We are working on your application</h2>} visible={applicationDialogVisible} style={{ width: window.innerWidth < 768 ? '' : '40vw', textAlign: "center" }}
                    onHide={() => { if (!applicationDialogVisible) return; setApplicationDialogVisible(false); }} draggable={false} closable={false} footer={footerContent}>
                    <p className="m-0">One of our associates is currently reviewing your application and will be in touch shortly.</p>
                    <p className="mt-5">In the meantime, if you have any questions, give us a call at <span className="brand-blue-text">1 (866) 592-2226</span> and one of our associates will assist you.</p>
                </Dialog>
            </main>
        </FadeTransition>
    );
}